import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useRenderTemplate } from '#/entities/template'
import { LINE_CAPABILITY, useDefaultLine } from '#/entities/telephony'
import type { useCreateToolbarItem } from '#/features/shipment-toolbar'
import { useGetShipmentApiV1ShipmentsShipmentIdGet } from '#/shared/api/generated/shipment/theCargoShipment'
import { getApiErrorDetail } from '#/shared/lib/api/error-handler'
import { usPhoneToE164 } from '#/shared/lib/format'
import { expandTemplateChips, htmlToMultilineText } from '#/shared/lib/html'
import { sanitizeSmsText } from '#/shared/lib/sms-sanitize'
import { toast } from '#/shared/ui/sonner'

import { useResetOnShipmentChange, useSeededList } from './reseed'

function useFromNumbers(armed: boolean) {
  // Share the one default-line selection so a text starts from the same line as
  // a call; the user can still override it for this message.
  const { lines, line } = useDefaultLine({
    capability: LINE_CAPABILITY.sms,
    enabled: armed,
  })
  const fromNumbers = useMemo(
    () => lines.map((entry) => entry.phone_number),
    [lines],
  )
  const preferred = line?.phone_number
  const [fromPhone, setFromPhone] = useState('')
  useEffect(() => {
    setFromPhone((current) => {
      if (current && fromNumbers.includes(current)) return current
      if (preferred && fromNumbers.includes(preferred)) return preferred
      return fromNumbers[0] ?? ''
    })
  }, [fromNumbers, preferred])
  // The deliverability preview endpoint identifies the sender line by its
  // UserPhone id (do_not_text is scoped per line), not the number.
  const fromPhoneId =
    lines.find((entry) => entry.phone_number === fromPhone)?.id ?? ''
  return { fromNumbers, fromPhone, fromPhoneId, setFromPhone }
}

function usePhoneContext(shipmentId: string | undefined, armed: boolean) {
  const shipmentQuery = useGetShipmentApiV1ShipmentsShipmentIdGet(
    shipmentId ?? '',
    { query: { enabled: armed && Boolean(shipmentId) } },
  )
  const customer = shipmentQuery.data?.data.customer
  return {
    customerPhone: customer?.phone ?? '',
    customerId: customer?.id,
  }
}

interface PhoneSliceDeps {
  shipmentId: string | undefined
  armed: boolean
  createSms: ReturnType<typeof useCreateToolbarItem>['createSms']
  isSending: boolean
  closeTab: () => void
}

/** Resolve a template against the shipment and load it into the composer.
 *  The render endpoint returns chips already carrying resolved values — show
 *  those values as plain text, not chips (chips are for token-only contexts:
 *  mass jobs, template editing). */
function useApplyTemplate(
  shipmentId: string | undefined,
  setBody: (body: string) => void,
) {
  const { t } = useTranslation()
  const renderTemplate = useRenderTemplate()
  return async (templateId: string, insertValues?: Record<string, string>) => {
    if (!shipmentId) return
    let rendered: string
    try {
      rendered = (await renderTemplate(templateId, shipmentId)).body
    } catch (error) {
      // The UI fires this handler without awaiting it, so an escaped rejection
      // would surface only as an unhandled-promise Sentry error.
      toast.error(
        getApiErrorDetail(error) ?? t('shipmentToolbar.create.templateError'),
      )
      return
    }
    setBody(expandTemplateChips(rendered, insertValues))
  }
}

export function usePhoneSlice({
  shipmentId,
  armed,
  createSms,
  isSending,
  closeTab,
}: PhoneSliceDeps) {
  const [body, setBody] = useState('')
  const ctx = usePhoneContext(shipmentId, armed)
  // Re-seeds the recipient from the customer on every row switch (the panel
  // stays mounted); clearing the body draft alongside so it can't ride to the
  // next customer.
  const [toPhones, setToPhones] = useSeededList(shipmentId, ctx.customerPhone)
  useResetOnShipmentChange(shipmentId, () => setBody(''))
  const { fromNumbers, fromPhone, fromPhoneId, setFromPhone } =
    useFromNumbers(armed)
  // The body the send would carry, for the deliverability preview. Chips expand
  // to their tokens here (their shipment values are only resolved at send, from
  // the editor's insert values), which the server's lint tolerates.
  const plainBody = useMemo(
    () => sanitizeSmsText(htmlToMultilineText(expandTemplateChips(body))),
    [body],
  )

  const reset = () => {
    setBody('')
    setToPhones(ctx.customerPhone ? [ctx.customerPhone] : [])
    closeTab()
  }

  const addPhone = (typed: string) => {
    const e164 = usPhoneToE164(typed)
    const normalized = e164 ?? typed.trim()
    if (!normalized) return
    setToPhones((prev) =>
      prev.includes(normalized) ? prev : [...prev, normalized],
    )
  }

  const removePhone = (id: string) => {
    setToPhones((prev) => prev.filter((value) => value !== id))
  }

  const applyTemplate = useApplyTemplate(shipmentId, setBody)

  const onSend = async (html: string, insertValues: Record<string, string>) => {
    if (!shipmentId || isSending) return
    // Resolve chips to their shipment-context values first, so the SMS
    // carries the actual link/value rather than the chip's display label.
    // Sanitized so a body of nothing but junk characters doesn't send.
    const text = sanitizeSmsText(
      htmlToMultilineText(expandTemplateChips(html, insertValues)),
    )
    if (!text || toPhones.length === 0) return
    try {
      await createSms({
        toPhones,
        body: text,
        fromPhone: fromPhone || undefined,
        customerId: ctx.customerId ?? undefined,
      })
    } catch {
      return // onError already surfaced the failure; keep the draft
    }
    reset()
  }

  return {
    value: body,
    plainBody,
    onChange: setBody,
    onCancel: reset,
    onSend,
    isSending,
    fromNumbers,
    fromPhone,
    fromPhoneId,
    setFromPhone,
    toPhones,
    addPhone,
    removePhone,
    applyTemplate,
    canSend: Boolean(shipmentId && toPhones.length > 0),
  }
}
